import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { crewQuery, rolesQuery } from '../api/crew.ts';
import { renderSafetyDocument } from '../api/documents.ts';
import { meQuery } from '../api/me.ts';
import { dayWithYear, tenantDay } from '../api/quotes.ts';
import {
  BAND_LABEL,
  CONSEQUENCE,
  LIKELIHOOD,
  bandOf,
  createSafetyDocument,
  jobSafetyQuery,
  publish,
  recordSignoff,
  renameSafetyDocument,
  retireSafetyDocument,
  safetyDocumentQuery,
  saveDraft,
  setJobSwms,
  setLinks,
  setSources,
  signoffsQuery,
  sourcesQuery,
  swmsQuery,
  type RiskBand,
  type RowInput,
  type SafetyDocument,
  type SafetyRow,
  type SafetySources,
} from '../api/safety.ts';
import { venuesQuery } from '../api/venues.ts';
import { icons } from '../components/icons.ts';
import { Button } from '../components/button.tsx';
import { AddButton, IconButton, RemoveButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { DocumentsPanel } from './documents-panel.tsx';

// CB-REQ CMP-02 and CMP-04 at v1.967, compliance part 3: SWMS and risk assessments, written as a
// table on the 5 × 5 matrix and published in versions that never change; where each SWMS applies;
// and who has signed. Flagged, never refused — the database works out who has not signed
// (shift_safety_gaps) and nothing here decides it.

const BAND_CLASS: Record<RiskBand, string> = {
  low: 'tag',
  medium: 'tag tag-accent',
  high: 'tag tag-accent-2',
  extreme: 'tag tag-extreme',
};

export function BandTag({ band }: { band: RiskBand }) {
  return <span className={BAND_CLASS[band]}>{BAND_LABEL[band]}</span>;
}

function useCan(permission: string): boolean {
  const me = useQuery(meQuery);
  return me.data?.capabilities.includes(permission) ?? false;
}

// — The list —

export function SafetyScreen() {
  const swms = useQuery(swmsQuery);
  const roles = useQuery(rolesQuery);
  const venues = useQuery(venuesQuery);
  const mayManage = useCan('safety.manage');
  const navigate = useNavigate();
  const [title, setTitle] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (t: string) => createSafetyDocument({ kind: 'swms', title: t }),
    onSuccess: (doc) => navigate({ to: '/safety/$documentId', params: { documentId: doc.id } }),
  });
  const roleName = (id: string) => roles.data?.find((r) => r.id === id)?.name ?? 'a role';
  const venueName = (id: string) => venues.data?.find((v) => v.id === id)?.name ?? 'a venue';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Compliance"
        title="SWMS"
        meta={
          swms.data ? `${swms.data.length} ${swms.data.length === 1 ? 'SWMS' : 'SWMS'}` : undefined
        }
        actions={
          mayManage && title === null ? (
            <AddButton noun="SWMS" onClick={() => setTitle('')} />
          ) : null
        }
      />
      <p className="max-w-prose text-sm text-ink-soft">
        Safe work method statements, written here as a table and signed by crew on their phone — or
        recorded by the office after a toolbox talk. A risk assessment belongs to one job and is
        written from that job. Nothing is refused over an unsigned SWMS; it is shown wherever the
        shift is.
      </p>
      {title !== null ? (
        <form
          className="panel flex flex-col gap-3"
          aria-label="New SWMS"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) create.mutate(title.trim());
          }}
        >
          <Field
            label="Title"
            hint="e.g. Rigging and flying truss"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          {create.isError ? (
            <p role="alert" className="alert">
              {create.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending}>
              Start
            </Button>
            <Button type="button" variant="quiet" onClick={() => setTitle(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {swms.isError ? (
        <p role="alert" className="alert">
          {swms.error.message}
        </p>
      ) : null}
      {swms.data?.length === 0 ? <p className="text-ink-soft">No SWMS yet.</p> : null}
      {swms.data && swms.data.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {swms.data.map((d) => (
            <li key={d.id} className="panel flex flex-col gap-1 p-4">
              <Link
                to="/safety/$documentId"
                params={{ documentId: d.id }}
                className="link font-semibold"
              >
                {d.title}
              </Link>
              <span className="text-sm text-ink-soft">
                {d.current ? `Version ${d.current.number}` : 'Not published yet'}
                {d.hasDraft && mayManage ? ' · a draft is in progress' : ''}
              </span>
              <span className="text-sm">
                {[
                  ...d.roleIds.map(roleName),
                  ...d.venueIds.map((v) => `at ${venueName(v)}`),
                  d.jobIds.length > 0
                    ? `${d.jobIds.length} ${d.jobIds.length === 1 ? 'job' : 'jobs'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Not linked to anything yet'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <SourcesSetting editable={mayManage} />
    </div>
  );
}

const SOURCE_LABEL: Record<keyof SafetySources, string> = {
  byRole: 'A SWMS linked to a role applies to that role’s shifts',
  byVenue: 'A SWMS linked to a venue applies to every shift there',
  byJob: 'A SWMS added to a job applies to that job’s shifts',
};

function SourcesSetting({ editable }: { editable: boolean }) {
  const queryClient = useQueryClient();
  const sources = useQuery(sourcesQuery);
  const save = useMutation({
    mutationFn: setSources,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sourcesQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
  if (!sources.data) return null;
  const current = sources.data;
  return (
    <fieldset className="flex flex-col gap-2" disabled={!editable || save.isPending}>
      <legend className="section-title">Which links count</legend>
      {(Object.keys(SOURCE_LABEL) as Array<keyof SafetySources>).map((key) => (
        <label key={key} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={current[key]}
            onChange={(e) => save.mutate({ ...current, [key]: e.currentTarget.checked })}
          />
          {SOURCE_LABEL[key]}
        </label>
      ))}
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
    </fieldset>
  );
}

// — One document —

export function SafetyDocumentScreen() {
  const { documentId } = useParams({ from: '/authenticated/safety/$documentId' });
  const doc = useQuery(safetyDocumentQuery(documentId));
  const mayManage = useCan('safety.manage');

  if (doc.isPending) return <p className="text-muted">Loading…</p>;
  if (doc.isError) {
    return (
      <p role="alert" className="alert">
        {doc.error.message}
      </p>
    );
  }
  const d = doc.data;
  return (
    <div className="flex flex-col gap-6">
      {d.kind === 'risk_assessment' && d.jobId ? (
        <Link to="/jobs/$jobId" params={{ jobId: d.jobId }} className="link self-start text-sm">
          ← Job
        </Link>
      ) : (
        <Link to="/safety" className="link self-start text-sm">
          ← SWMS
        </Link>
      )}
      <PageHeader
        kicker={d.kind === 'swms' ? 'SWMS' : 'Risk assessment'}
        title={d.title}
        meta={d.current ? `Version ${d.current.number}` : 'Not published yet'}
      />
      {mayManage ? <TitleAndRetire doc={d} /> : null}
      <section aria-labelledby="current" className="flex flex-col gap-3">
        <h2 id="current" className="section-title">
          {d.current ? `Version ${d.current.number}, as crew sign it` : 'Nothing published yet'}
        </h2>
        {d.current ? <RiskTable rows={d.currentRows} /> : null}
      </section>
      {/* Printed with a sign-on sheet for a toolbox talk on paper; record those signatures below. */}
      {d.current ? (
        <DocumentsPanel
          owner="safety_document"
          id={d.id}
          actions={[
            {
              label: `Print version ${d.current.number}`,
              prepare: () => renderSafetyDocument(d.id),
            },
          ]}
        />
      ) : null}
      {mayManage ? <DraftEditor doc={d} /> : null}
      {d.kind === 'swms' ? <Links doc={d} editable={mayManage} /> : null}
      <Signoffs doc={d} editable={mayManage} />
    </div>
  );
}

function TitleAndRetire({ doc }: { doc: SafetyDocument }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: (t: string) => renameSafetyDocument(doc.id, t),
    onSuccess: async () => {
      setTitle(null);
      await queryClient.invalidateQueries({ queryKey: ['safety'] });
    },
  });
  const retire = useMutation({
    mutationFn: () => retireSafetyDocument(doc.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['safety'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await (doc.jobId
        ? navigate({ to: '/jobs/$jobId', params: { jobId: doc.jobId } })
        : navigate({ to: '/safety' }));
    },
  });
  if (title !== null) {
    return (
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) rename.mutate(title.trim());
        }}
      >
        <Field label="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
        <Button type="submit" disabled={rename.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={() => setTitle(null)}>
          Cancel
        </Button>
      </form>
    );
  }
  return (
    <p className="flex flex-wrap gap-4 text-sm">
      <button type="button" className="link" onClick={() => setTitle(doc.title)}>
        Rename
      </button>
      <button
        type="button"
        className="link"
        onClick={() => {
          if (window.confirm(`Retire “${doc.title}”? No shift will need it; its sign-offs stay.`)) {
            retire.mutate();
          }
        }}
      >
        Retire
      </button>
      {retire.isError ? <span className="text-accent-2-700">{retire.error.message}</span> : null}
    </p>
  );
}

/** A version's table, read-only: steps, hazards, controls and the ratings before and after. */
export function RiskTable({ rows }: { rows: readonly SafetyRow[] }) {
  if (rows.length === 0) return <p className="text-sm text-ink-soft">No rows.</p>;
  return (
    <ol className="flex flex-col gap-3">
      {rows.map((r, i) => (
        <li key={i} className="panel flex flex-col gap-2 p-4 text-sm text-ink">
          <p className="font-semibold">
            {i + 1}. {r.step}
          </p>
          <p>
            <span className="text-ink-soft">Hazards: </span>
            {r.hazards}
          </p>
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-ink-soft">Risk before controls:</span>
            <BandTag band={r.bandBefore} />
            <span className="text-xs text-ink-soft">
              {LIKELIHOOD[r.likelihoodBefore - 1]} × {CONSEQUENCE[r.consequenceBefore - 1]}
            </span>
          </p>
          <p>
            <span className="text-ink-soft">Controls: </span>
            {r.controls}
          </p>
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-ink-soft">Risk after controls:</span>
            <BandTag band={r.bandAfter} />
            <span className="text-xs text-ink-soft">
              {LIKELIHOOD[r.likelihoodAfter - 1]} × {CONSEQUENCE[r.consequenceAfter - 1]}
            </span>
          </p>
          {r.responsible ? (
            <p>
              <span className="text-ink-soft">Responsible: </span>
              {r.responsible}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

const EMPTY_ROW: RowInput = {
  step: '',
  hazards: '',
  likelihoodBefore: 3,
  consequenceBefore: 3,
  controls: '',
  likelihoodAfter: 1,
  consequenceAfter: 3,
  responsible: null,
};

const toInput = (r: SafetyRow): RowInput => ({
  step: r.step,
  hazards: r.hazards,
  likelihoodBefore: r.likelihoodBefore,
  consequenceBefore: r.consequenceBefore,
  controls: r.controls,
  likelihoodAfter: r.likelihoodAfter,
  consequenceAfter: r.consequenceAfter,
  responsible: r.responsible ?? null,
});

/**
 * The draft: rows edited here, saved as a whole, and published when ready. Publishing asks whether
 * people who signed before must sign again (CMP-02 at v1.967).
 */
function DraftEditor({ doc }: { doc: SafetyDocument }) {
  const queryClient = useQueryClient();
  const starting = doc.draftRows ?? doc.currentRows;
  const [rows, setRows] = useState<RowInput[] | null>(null);
  const [resign, setResign] = useState(true);
  const [problem, setProblem] = useState<string | undefined>();
  const editing = rows ?? starting.map(toInput);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['safety'] });
  const save = useMutation({
    mutationFn: (list: RowInput[]) => saveDraft(doc.id, list),
    onSuccess: async () => {
      setRows(null);
      await refresh();
    },
  });
  const release = useMutation({
    mutationFn: async () => {
      if (rows) await saveDraft(doc.id, rows);
      await publish(doc.id, doc.current ? resign : true);
    },
    onSuccess: async () => {
      setRows(null);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  const change = (i: number, patch: Partial<RowInput>) =>
    setRows(editing.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const incomplete = editing.some((r) => !r.step.trim() || !r.hazards.trim() || !r.controls.trim());

  function submit(e: FormEvent) {
    e.preventDefault();
    if (incomplete) return setProblem('Each row needs its step, hazards and controls.');
    setProblem(undefined);
    save.mutate(editing);
  }

  return (
    <section aria-labelledby="draft" className="flex flex-col gap-3">
      <h2 id="draft" className="section-title">
        {doc.draftRows ? 'Draft' : 'Write the next version'}
      </h2>
      <p className="text-sm text-ink-soft">
        Crew sign the published version. Changes here make a draft; nothing changes for crew until
        you publish it.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Draft">
        <ol className="flex flex-col gap-3">
          {editing.map((r, i) => (
            <RowEditor
              key={i}
              index={i}
              row={r}
              onChange={(patch) => change(i, patch)}
              onRemove={() => setRows(editing.filter((_, j) => j !== i))}
              onMove={(by) => {
                const next = [...editing];
                const [moved] = next.splice(i, 1);
                next.splice(i + by, 0, moved!);
                setRows(next);
              }}
              first={i === 0}
              last={i === editing.length - 1}
            />
          ))}
        </ol>
        <AddButton
          noun="Step"
          type="button"
          variant="quiet"
          className="self-start"
          onClick={() => setRows([...editing, { ...EMPTY_ROW }])}
        />
        {problem || save.isError ? (
          <p role="alert" className="alert">
            {problem ?? save.error?.message}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending || rows === null}>
            Save draft
          </Button>
          {rows !== null ? (
            <Button type="button" variant="quiet" onClick={() => setRows(null)}>
              Discard
            </Button>
          ) : null}
        </div>
      </form>

      <div className="flex flex-col gap-2 rounded-md bg-tile p-3">
        <p className="text-sm font-semibold">Publish as version {(doc.current?.number ?? 0) + 1}</p>
        {doc.current ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={resign}
              onChange={(e) => setResign(e.currentTarget.checked)}
            />
            <span>
              Everyone signs again — a new hazard or a changed control. Untick for a wording fix:
              signatures of version {doc.current.number} then still count.
            </span>
          </label>
        ) : null}
        {release.isError ? (
          <p role="alert" className="alert">
            {release.error.message}
          </p>
        ) : null}
        <Button
          type="button"
          className="self-start"
          disabled={release.isPending || incomplete || editing.length === 0}
          onClick={() => release.mutate()}
        >
          Publish
        </Button>
      </div>
    </section>
  );
}

function RowEditor({
  index,
  row,
  onChange,
  onRemove,
  onMove,
  first,
  last,
}: {
  index: number;
  row: RowInput;
  onChange: (patch: Partial<RowInput>) => void;
  onRemove: () => void;
  onMove: (by: -1 | 1) => void;
  first: boolean;
  last: boolean;
}) {
  const n = index + 1;
  return (
    <li className="panel flex flex-col gap-3 p-4" aria-label={`Step ${n}`}>
      <Field
        label={`Step ${n}`}
        value={row.step}
        onChange={(e) => onChange({ step: e.currentTarget.value })}
      />
      <TextArea
        id={`hazards-${n}`}
        label="Hazards"
        value={row.hazards}
        onChange={(hazards) => onChange({ hazards })}
      />
      <Rating
        id={`before-${n}`}
        label="Risk before controls"
        likelihood={row.likelihoodBefore}
        consequence={row.consequenceBefore}
        onChange={(l, c) => onChange({ likelihoodBefore: l, consequenceBefore: c })}
      />
      <TextArea
        id={`controls-${n}`}
        label="Controls"
        value={row.controls}
        onChange={(controls) => onChange({ controls })}
      />
      <Rating
        id={`after-${n}`}
        label="Risk after controls"
        likelihood={row.likelihoodAfter}
        consequence={row.consequenceAfter}
        onChange={(l, c) => onChange({ likelihoodAfter: l, consequenceAfter: c })}
      />
      <Field
        label="Responsible (optional)"
        value={row.responsible ?? ''}
        onChange={(e) => onChange({ responsible: e.currentTarget.value || null })}
      />
      <p className="flex flex-wrap gap-4 text-sm">
        {!first ? (
          <IconButton icon={icons.moveUp} label={`Move up step ${n}`} onClick={() => onMove(-1)} />
        ) : null}
        {!last ? (
          <IconButton
            icon={icons.moveDown}
            label={`Move down step ${n}`}
            onClick={() => onMove(1)}
          />
        ) : null}
        <RemoveButton what={`step ${n}`} onClick={onRemove} />
      </p>
    </li>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs text-ink-soft">
        {label}
      </label>
      <textarea
        id={id}
        className="input min-h-20"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  );
}

function Rating({
  id,
  label,
  likelihood,
  consequence,
  onChange,
}: {
  id: string;
  label: string;
  likelihood: number;
  consequence: number;
  onChange: (likelihood: number, consequence: number) => void;
}) {
  return (
    <fieldset className="flex flex-wrap items-end gap-3">
      <legend className="mb-1 text-xs text-ink-soft">{label}</legend>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${id}-l`} className="text-xs text-ink-soft">
          Likelihood
        </label>
        <select
          id={`${id}-l`}
          className="input"
          value={likelihood}
          onChange={(e) => onChange(Number(e.currentTarget.value), consequence)}
        >
          {LIKELIHOOD.map((name, i) => (
            <option key={name} value={i + 1}>
              {i + 1} {name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${id}-c`} className="text-xs text-ink-soft">
          Consequence
        </label>
        <select
          id={`${id}-c`}
          className="input"
          value={consequence}
          onChange={(e) => onChange(likelihood, Number(e.currentTarget.value))}
        >
          {CONSEQUENCE.map((name, i) => (
            <option key={name} value={i + 1}>
              {i + 1} {name}
            </option>
          ))}
        </select>
      </div>
      <span className="pb-2" aria-live="polite">
        <BandTag band={bandOf(likelihood, consequence)} />
      </span>
    </fieldset>
  );
}

/** CMP-02 at v1.967: the roles and venues a SWMS applies to. */
function Links({ doc, editable }: { doc: SafetyDocument; editable: boolean }) {
  const queryClient = useQueryClient();
  const roles = useQuery(rolesQuery);
  const venues = useQuery(venuesQuery);
  const [editing, setEditing] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [venueIds, setVenueIds] = useState<string[]>([]);
  const save = useMutation({
    mutationFn: () => setLinks(doc.id, roleIds, venueIds),
    onSuccess: async () => {
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['safety'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });
  const names = (ids: readonly string[], list: Array<{ id: string; name: string }> | undefined) =>
    ids.map((id) => list?.find((x) => x.id === id)?.name ?? '').filter(Boolean);
  const toggle = (set: (f: (c: string[]) => string[]) => void, id: string, on: boolean) =>
    set((c) => (on ? [...c, id] : c.filter((x) => x !== id)));

  return (
    <section aria-labelledby="applies" className="flex flex-col gap-3">
      <h2 id="applies" className="section-title">
        Where it applies
      </h2>
      {!editing ? (
        <>
          <p className="text-sm">
            <span className="text-ink-soft">Roles: </span>
            {names(doc.roleIds, roles.data).join(', ') || 'none'}
          </p>
          <p className="text-sm">
            <span className="text-ink-soft">Venues: </span>
            {names(doc.venueIds, venues.data).join(', ') || 'none'}
          </p>
          <p className="text-sm text-ink-soft">
            Added to {doc.jobIds.length} {doc.jobIds.length === 1 ? 'job' : 'jobs'} — add it from
            the job.
          </p>
          {editable ? (
            <button
              type="button"
              className="link self-start text-sm"
              onClick={() => {
                setRoleIds([...doc.roleIds]);
                setVenueIds([...doc.venueIds]);
                setEditing(true);
              }}
            >
              Applies to…
            </button>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col gap-3 rounded-md bg-tile p-3">
          <fieldset className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <legend className="mb-1 text-sm font-semibold">Roles</legend>
            {(roles.data ?? []).map((r) => (
              <label key={r.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={roleIds.includes(r.id)}
                  onChange={(e) => toggle(setRoleIds, r.id, e.currentTarget.checked)}
                />
                {r.name}
              </label>
            ))}
          </fieldset>
          <fieldset className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <legend className="mb-1 text-sm font-semibold">Venues</legend>
            {(venues.data ?? []).map((v) => (
              <label key={v.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={venueIds.includes(v.id)}
                  onChange={(e) => toggle(setVenueIds, v.id, e.currentTarget.checked)}
                />
                {v.name}
              </label>
            ))}
          </fieldset>
          {save.isError ? (
            <p role="alert" className="alert">
              {save.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Who signed which version, and the office recording one after a toolbox talk. */
function Signoffs({ doc, editable }: { doc: SafetyDocument; editable: boolean }) {
  const queryClient = useQueryClient();
  const signoffs = useQuery(signoffsQuery(doc.id));
  const crew = useQuery({ ...crewQuery(), enabled: true });
  const zone = useQuery(meQuery).data?.tenant.defaultTz;
  const [recording, setRecording] = useState(false);
  const [crewMemberId, setCrewMemberId] = useState('');
  const [note, setNote] = useState('Signed on paper at the toolbox talk');
  const record = useMutation({
    mutationFn: () => recordSignoff(doc.id, crewMemberId, note.trim()),
    onSuccess: async () => {
      setRecording(false);
      setCrewMemberId('');
      await queryClient.invalidateQueries({ queryKey: ['safety'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });
  const name = (id: string) => crew.data?.find((c) => c.id === id)?.displayName ?? 'Someone';

  return (
    <section aria-labelledby="signed" className="flex flex-col gap-3">
      <h2 id="signed" className="section-title">
        Signed
      </h2>
      {signoffs.data?.length === 0 ? <p className="text-sm text-ink-soft">Nobody yet.</p> : null}
      <ul className="flex flex-col gap-1 text-sm">
        {signoffs.data?.map((s, i) => (
          <li key={i}>
            <span className="font-semibold">{name(s.crewMemberId)}</span> — version{' '}
            {s.versionNumber}
            {zone ? `, ${dayWithYear(tenantDay(s.signedAt, zone))}` : null}
            {s.method === 'office' ? (
              <span className="text-ink-soft"> · recorded by the office: {s.note}</span>
            ) : (
              <span className="text-ink-soft"> · on their phone</span>
            )}
          </li>
        ))}
      </ul>
      {editable && doc.current && !recording ? (
        <button
          type="button"
          className="link self-start text-sm"
          aria-label="Add sign-off"
          onClick={() => setRecording(true)}
        >
          <icons.add size={14} strokeWidth={2.5} aria-hidden className="mr-1 inline" />
          Sign-off
        </button>
      ) : null}
      {recording ? (
        <form
          className="flex flex-col gap-3 rounded-md bg-tile p-3"
          aria-label="Record a sign-off"
          onSubmit={(e) => {
            e.preventDefault();
            if (crewMemberId && note.trim()) record.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="signoff-who" className="text-xs text-ink-soft">
              Who signed version {doc.current?.number}
            </label>
            <select
              id="signoff-who"
              className="input"
              value={crewMemberId}
              onChange={(e) => setCrewMemberId(e.currentTarget.value)}
            >
              <option value="">Choose…</option>
              {(crew.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="How it was signed"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          {record.isError ? (
            <p role="alert" className="alert">
              {record.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={record.isPending}>
              Record
            </Button>
            <Button type="button" variant="quiet" onClick={() => setRecording(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

// — On a job —

/**
 * CMP-02 / CMP-04 at v1.967: a job's safety paperwork — whether its shifts need SWMS, which SWMS
 * were added to it, and its risk assessments.
 */
export function JobSafetyPanel({
  jobId,
  swmsRequired,
  onSwitch,
  switching,
  mayManageJob,
}: {
  jobId: string;
  swmsRequired: boolean;
  onSwitch: (on: boolean) => void;
  switching: boolean;
  mayManageJob: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mayManage = useCan('safety.manage');
  const safety = useQuery(jobSafetyQuery(jobId));
  const swms = useQuery(swmsQuery);
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [raTitle, setRaTitle] = useState<string | null>(null);
  const saveSwms = useMutation({
    mutationFn: () => setJobSwms(jobId, chosen),
    onSuccess: async () => {
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['safety'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
  const createRa = useMutation({
    mutationFn: (title: string) => createSafetyDocument({ kind: 'risk_assessment', title, jobId }),
    onSuccess: (doc) => navigate({ to: '/safety/$documentId', params: { documentId: doc.id } }),
  });
  const titleOf = (id: string) => swms.data?.find((d) => d.id === id)?.title ?? '';

  return (
    <section className="flex flex-col gap-3" aria-labelledby="job-safety">
      <h2 id="job-safety" className="section-title">
        SWMS and risk assessments
      </h2>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={swmsRequired}
          disabled={!mayManageJob || switching}
          onChange={(e) => onSwitch(e.currentTarget.checked)}
        />
        Crew on this job sign SWMS
      </label>
      {swmsRequired ? (
        !editing ? (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-soft">Added to this job:</span>
            {safety.data?.swmsIds.length === 0 ? <span>none</span> : null}
            {safety.data?.swmsIds.map((id) => (
              <Link key={id} to="/safety/$documentId" params={{ documentId: id }} className="tag">
                {titleOf(id)}
              </Link>
            ))}
            {mayManage ? (
              <button
                type="button"
                className="link"
                onClick={() => {
                  setChosen([...(safety.data?.swmsIds ?? [])]);
                  setEditing(true);
                }}
              >
                Edit SWMS<span className="sr-only"> added to this job</span>
              </button>
            ) : null}
          </p>
        ) : (
          <fieldset className="flex flex-col gap-2 rounded-md bg-tile p-3">
            <legend className="text-sm font-semibold">SWMS for this job</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              {(swms.data ?? []).map((d) => (
                <label key={d.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={chosen.includes(d.id)}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setChosen((c) => (on ? [...c, d.id] : c.filter((x) => x !== d.id)));
                    }}
                  />
                  {d.title}
                </label>
              ))}
            </div>
            {saveSwms.isError ? (
              <p role="alert" className="alert">
                {saveSwms.error.message}
              </p>
            ) : null}
            <div className="flex gap-2">
              <Button type="button" disabled={saveSwms.isPending} onClick={() => saveSwms.mutate()}>
                Save
              </Button>
              <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </fieldset>
        )
      ) : (
        <p className="text-sm text-ink-soft">
          No SWMS asked of this job’s crew. A risk assessment below still applies.
        </p>
      )}
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-soft">Risk assessments:</span>
        {safety.data?.riskAssessments.length === 0 ? <span>none</span> : null}
        {safety.data?.riskAssessments.map((d) => (
          <Link key={d.id} to="/safety/$documentId" params={{ documentId: d.id }} className="tag">
            {d.title}
            {d.current ? ` v${d.current.number}` : ' (draft)'}
          </Link>
        ))}
        {mayManage && raTitle === null ? (
          <button
            type="button"
            className="link"
            aria-label="Add Risk assessment"
            onClick={() => setRaTitle('')}
          >
            <icons.add size={14} strokeWidth={2.5} aria-hidden className="mr-1 inline" />
            Risk assessment
          </button>
        ) : null}
      </p>
      {raTitle !== null ? (
        <form
          className="flex flex-wrap items-end gap-2"
          aria-label="New risk assessment"
          onSubmit={(e) => {
            e.preventDefault();
            if (raTitle.trim()) createRa.mutate(raTitle.trim());
          }}
        >
          <Field
            label="Risk assessment title"
            value={raTitle}
            onChange={(e) => setRaTitle(e.currentTarget.value)}
          />
          <Button type="submit" disabled={createRa.isPending}>
            Start
          </Button>
          <Button type="button" variant="quiet" onClick={() => setRaTitle(null)}>
            Cancel
          </Button>
        </form>
      ) : null}
    </section>
  );
}
